import { Component, h, Host, Prop, State, Method, Element, Build, Event, EventEmitter, Watch } from '@stencil/core';
import { getAuthUrl, PRODUCTION_DOMAIN, DEVELOPMENT_DOMAIN } from '../../social-auth/shared/index';

const COMPONENT_NAME = 'sn-popup-type-1';

// Event other components can dispatch on `window` to open this popup.
const OPEN_EVENT = 'sn-popup-type-1-open';

/**
 * Centered success dialog shown after an action completes (e.g. a completed
 * signNow registration). Renders a green check icon, a heading, and two link
 * actions — a secondary link (e.g. "Check signNow") and a primary link (e.g.
 * "Back to form editing"). Both links open in a new tab.
 *
 * Opens via the `open()` method or by dispatching the `sn-popup-type-1-open`
 * event on `window` (optionally with `detail: { formId }`). Closes on the close
 * icon, an overlay click, or the Escape key. Blocks body scrolling while visible.
 *
 * On open it resolves the primary action's target: the form id is POSTed to
 * `editor-data-url` and the returned `editorUrl` becomes the anchor's `href`, so
 * the click itself is a plain link into the form editor in a new tab.
 *
 * @product signnow
 * @version 1.0.0
 * @category SignNow
 *
 * @example
 *   <sn-popup-type-1
 *     heading="You have been successfully registered in signNow"
 *     secondary-text="Check signNow"
 *     primary-text="Back to form editing"
 *   ></sn-popup-type-1>
 */
@Component({
  tag: 'sn-popup-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnPopupType1 {
  @Element() host!: HTMLElement;

  /**
   * Color mode for theming.
   * @default "signnow"
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Controls dialog visibility.
   * @default false
   */
  @Prop({ mutable: true, reflect: true }) isOpen: boolean = false;

  /**
   * Success message shown below the icon.
   * @content
   * @dynamic
   * @default "You have been successfully registered in signNow"
   */
  @Prop({ attribute: 'heading' }) heading: string = 'You have been successfully registered in signNow';

  /**
   * Label for the secondary (link) action.
   * @content
   * @dynamic
   * @default "Check signNow"
   */
  @Prop({ attribute: 'secondary-text' }) secondaryText: string = 'Check signNow';

  /**
   * URL the secondary action links to. Omit it to link to the web app of the current environment;
   * set it to an empty string to hide the secondary action. A value pointing at one of the auth
   * hosts is re-homed to the current environment — see `resolveSecondaryHref`.
   */
  @Prop({ attribute: 'secondary-href' }) secondaryHref?: string;

  /**
   * Label for the primary action.
   * @content
   * @dynamic
   * @default "Back to form editing"
   */
  @Prop({ attribute: 'primary-text' }) primaryText: string = 'Back to form editing';

  /**
   * Form identifier used to resolve the editor URL for the primary action. When empty it is read
   * from the first `[data-formid]` element on the page, like the legacy `createLinkEditorModal()`.
   */
  @Prop({ attribute: 'form-id', mutable: true, reflect: true }) formId?: string;

  /**
   * Endpoint that exchanges a form id for the editor URL. A path is resolved against the main site
   * host rather than the current one (see `resolveEditorDataUrl`); pass an absolute URL to pin it.
   * @default "/forms/get-editor-data"
   */
  @Prop({ attribute: 'editor-data-url' }) editorDataUrl: string = '/forms/get-editor-data';

  /** Editor URL resolved when the dialog opens; becomes the primary action's `href`. */
  @State() editorUrl?: string;

  /** Fired when the dialog is closed (close icon, overlay, or Escape). */
  @Event() snPopupClose!: EventEmitter<void>;

  @Watch('isOpen')
  watchIsOpen(open: boolean) {
    if (!Build.isBrowser) return;
    if (open) this.lockBody();
    else this.unlockBody();
  }

  connectedCallback() {
    if (!Build.isBrowser) return;
    window.addEventListener(OPEN_EVENT, this.handleOpenEvent);
    document.addEventListener('keydown', this.handleKeyDown);
    if (this.isOpen) this.lockBody();
  }

  disconnectedCallback() {
    if (!Build.isBrowser) return;
    window.removeEventListener(OPEN_EVENT, this.handleOpenEvent);
    document.removeEventListener('keydown', this.handleKeyDown);
    this.unlockBody();
  }

  /** Opens the dialog. */
  @Method()
  async open() {
    this.isOpen = true;
    this.resolveEditorUrl();
  }

  /** Closes the dialog. */
  @Method()
  async close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.snPopupClose.emit();
  }

  /**
   * The openers (`sn-social-auth-type-2`, `sn-google-one-tap-type-2`) pass the form id in
   * `detail`, so the primary action does not have to scrape the DOM for it. A bare event with no
   * detail stays valid — `form-id`/`[data-formid]` remain the fallbacks.
   */
  private handleOpenEvent = (event: Event) => {
    const detail = (event as CustomEvent<{ formId?: string } | undefined>).detail;
    if (detail?.formId) this.formId = detail.formId;
    this.isOpen = true;
    this.resolveEditorUrl();
  };

  /**
   * Fetches the editor URL for the primary action, mirroring the legacy `createLinkEditorModal()`:
   * POST the form id to `/forms/get-editor-data` and put the returned `editorUrl` straight into the
   * anchor's `href`. Resolved when the dialog opens rather than on click, so the anchor is a plain
   * link by the time the user presses it — no interception, no popup blocker, opens in a new tab.
   *
   * Silent on failure: the anchor keeps `href="#"` exactly as the legacy markup shipped it.
   */
  /**
   * Target of the "Check signNow" action, always in the page's own environment: `app.signnow.com`
   * from production, `app.signnow-rc.xyz` from anywhere else.
   *
   * Omitting `secondary-href` yields the environment's web app. A value is normally used as given,
   * except when it points at one of the auth hosts — then only its host is swapped for the current
   * environment's. That rewrite exists for pages already published with a hardcoded
   * `https://app.signnow.com`, which would otherwise send an RC user into production.
   *
   * An explicit empty string still hides the action, so `secondary-href=""` keeps working.
   */
  private resolveSecondaryHref(): string | undefined {
    if (this.secondaryHref === undefined) return Build.isBrowser ? getAuthUrl() : undefined;
    if (!this.secondaryHref) return undefined;
    if (!Build.isBrowser) return this.secondaryHref;

    try {
      const url = new URL(this.secondaryHref, window.location.href);
      const authHosts = [`app${PRODUCTION_DOMAIN}`, `app${DEVELOPMENT_DOMAIN}`];
      if (authHosts.includes(url.hostname)) {
        url.protocol = 'https:';
        url.hostname = new URL(getAuthUrl()).hostname;
        return url.toString();
      }
    } catch {
      // Not a parseable URL — leave whatever was configured untouched.
    }

    return this.secondaryHref;
  }

  /**
   * Absolute endpoint for the editor lookup. Forms pages are served from subdomains
   * (`forms-library.signnow-rc.xyz`, …) but `/forms/get-editor-data` only exists on the main site,
   * so a relative path would 404 on the subdomain. Collapsing the host to `www.<registrable
   * domain>` keeps the request in the page's own environment — an RC page asks RC, a production
   * page asks production. Same normalisation `sn-popup-form-loader` already uses.
   */
  private resolveEditorDataUrl(): string {
    if (this.editorDataUrl.startsWith('http')) return this.editorDataUrl;

    const { protocol, hostname } = window.location;
    const parts = hostname.split('.');
    const normalizedHost = parts.length > 2 ? `www.${parts.slice(-2).join('.')}` : hostname;

    return `${protocol}//${normalizedHost}${this.editorDataUrl}`;
  }

  private resolveEditorUrl() {
    if (!Build.isBrowser) return;
    if (this.editorUrl) return;

    const formId = this.formId || (document.querySelector('section[data-formid], [data-formid]') as HTMLElement)?.dataset?.formid;
    if (!formId) return;

    const csrf = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content || '';

    // Form-encoded, not JSON: the endpoint reads the id with Laravel's `$request->post('id')`,
    // which looks at the form parameter bag and stays empty for a JSON body. This is also what
    // `sn-popup-form-loader` sends against the same endpoint.
    fetch(this.resolveEditorDataUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-CSRF-TOKEN': csrf },
      body: `id=${encodeURIComponent(formId)}`,
      credentials: 'include',
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(({ editorUrl }) => {
        if (editorUrl) this.editorUrl = editorUrl;
      })
      .catch(error => {
        console.error('Failed to resolve the form editor URL:', error);
      });
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    if (this.isOpen && e.key === 'Escape') {
      e.preventDefault();
      this.close();
    }
  };

  private lockBody() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.classList.add(`${COMPONENT_NAME}-open`);
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
  }

  private unlockBody() {
    document.body.classList.remove(`${COMPONENT_NAME}-open`);
    document.body.style.paddingRight = '';
  }

  render() {
    const headingId = `${COMPONENT_NAME}-heading`;
    const secondaryHref = this.resolveSecondaryHref();

    return (
      <Host data-color-mode={this.colorMode}>
        <div
          class={{
            [COMPONENT_NAME]: true,
            [`${COMPONENT_NAME}--visible`]: this.isOpen,
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
        >
          <button
            type="button"
            class={`${COMPONENT_NAME}__overlay`}
            aria-label="Close modal"
            onClick={() => this.close()}
          ></button>

          <div class={`${COMPONENT_NAME}__body`}>
            <button
              type="button"
              class={`${COMPONENT_NAME}__close`}
              aria-label="Close modal"
              onClick={() => this.close()}
            >
              <me-icon name="interface--x" inline width="32" height="32"></me-icon>
            </button>

            <div class={`${COMPONENT_NAME}__icon`}>
              <me-icon
                class={`${COMPONENT_NAME}__icon-svg`}
                name="interface--check-circle"
                inline
                width="50"
                height="50"
              ></me-icon>
            </div>

            <h2 class={`${COMPONENT_NAME}__heading`} id={headingId}>
              {this.heading}
            </h2>

            <div class={`${COMPONENT_NAME}__actions`}>
              {secondaryHref && (
                <me-button class={`${COMPONENT_NAME}__action`} variant="secondary" tag="a" href={secondaryHref} target="_blank" rel="noopener noreferrer">
                  {this.secondaryText}
                </me-button>
              )}
              {/* `js-editor-link` mirrors the legacy `js-editor-modal-link` anchor: the href is
                  filled in with the resolved editor URL and opens in a new tab. */}
              <me-button
                class={`${COMPONENT_NAME}__action js-editor-link`}
                variant="primary"
                tag="a"
                href={this.editorUrl || '#'}
                target={this.editorUrl ? '_blank' : undefined}
                rel={this.editorUrl ? 'noopener noreferrer' : undefined}
              >
                {this.primaryText}
              </me-button>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
